import store from '@/store'
import axios from 'axios'
import heic2any from 'heic2any'
import Cookies from 'js-cookie'
import router from '@/router'

// Import PDF.js using require to avoid webpack module parsing issues
const pdfjsLib = require('pdfjs-dist/legacy/build/pdf.js')

// Configure PDF.js worker for the legacy build
pdfjsLib.GlobalWorkerOptions.workerSrc = '//cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js'

export function isLocalDev () {
  const h = window.location.hostname
  return h === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(h)
}

const MAX_IMAGE_SIZE = 960
const IMAGE_QUALITY = 1.0

export const debounce = function debounce(func, wait, immediate) {
  var timeout
  return function () {
    var context = this, args = arguments
    var later = function () {
      timeout = null
      if (!immediate) func.apply(context, args)
    };
    var callNow = immediate && !timeout
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
    if (callNow) func.apply(context, args)
  }
}

export const logEvent = (name, count = 0, duration = 0, params = {}) => {
  if (typeof count === 'object') {
    params = count
    count = 0
  } else if (typeof duration === 'object') {
    params = duration
    duration = 0
  }

  if (process.env.VUE_APP_ENV === 'test') {
    let paramsDisplay = ''
    Object.keys(params).forEach(k => {
      paramsDisplay += `${k}=${params[k]} `
    })
    console.log(`[ANALYTICS] logEvent: web.${name} ${paramsDisplay}`)
  }
  let data = {
    name: 'web.' + name,
    device_id: store.state.user.deviceId,
    user_id: store.state.user.user ? store.state.user.user.id : 0,
    company_id: store.state.company.company ? store.state.company.company.id : 0,
    count: count,
    duration: duration,
    params: params
  }
  if (process.env.VUE_APP_ENV === 'prod') {
    axios.post(`${process.env.VUE_APP_ANALYTICS_URL}/log`, data)
  }
}

/**
 * Resize image from a file. Default max size is 960px defined at MAX_IMAGE_SIZE.
 * @param file - File of the image.
 * @param name - Name for the resized image.
 * @return Resized image with dataURL & file.
 */
export const resizeImage = async (file, name) => {
  let preprocessFile = file
  if (file.type === 'image/heic') {
    preprocessFile = await convertHeicToJpeg(file, name)
  }
  return new Promise(resolve => {
    const fileReader = new FileReader()
    fileReader.readAsDataURL(preprocessFile)
    fileReader.onload = e => {
      const image = new Image()
      image.src = e.target.result
      image.onload = () => {
        const canvas = document.createElement('canvas')
        const width = image.width
        const height = image.height
        let resizeFactor = 1.0
        if (width > MAX_IMAGE_SIZE || height > MAX_IMAGE_SIZE) {
          if (height > width) {
            resizeFactor = MAX_IMAGE_SIZE / height
          } else {
            resizeFactor = MAX_IMAGE_SIZE / width
          }
        }
        const resizeWidth = width * resizeFactor
        const resizeHeight = height * resizeFactor
        canvas.width = resizeWidth
        canvas.height = resizeHeight
        canvas.getContext('2d').drawImage(image, 0, 0, resizeWidth, resizeHeight)
        const dataURL = canvas.toDataURL('image/jpeg')
        canvas.toBlob((blob) => {
          const file = new File([blob], name, {
            type: 'image/jpeg',
            lastModified: Date.now()
          })
          resolve({dataURL, file})
        }, 'image/jpeg', IMAGE_QUALITY)
      }
    }
  })
}

const convertHeicToJpeg = async (file, name) => {
  const blob = await heic2any({
    blob: file,
    toType: 'image/jpeg',
    quality: IMAGE_QUALITY
  })
  return new File([blob], name)
}

/**
 * Fix image orientation based on EXIF data without resizing.
 * Uses createImageBitmap with imageOrientation to handle EXIF rotation.
 * @param file - File of the image
 * @returns Orientation-corrected file
 */
export const fixImageOrientation = async (file) => {
  // Skip non-image files
  if (!file.type.startsWith('image/')) {
    return file
  }

  try {
    // Use createImageBitmap with imageOrientation to apply EXIF rotation
    const imageBitmap = await createImageBitmap(file, {
      imageOrientation: 'from-image'
    })

    const canvas = document.createElement('canvas')
    canvas.width = imageBitmap.width
    canvas.height = imageBitmap.height
    canvas.getContext('2d').drawImage(imageBitmap, 0, 0)

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        const correctedFile = new File([blob], file.name, {
          type: file.type,
          lastModified: Date.now()
        })
        resolve(correctedFile)
      }, file.type)
    })
  } catch (error) {
    // If createImageBitmap fails, return original file
    console.warn('Failed to fix image orientation:', error)
    return file
  }
}

export const setupLanguage = () => {
  const key = "yz_language"
  let domain = `.${process.env.VUE_APP_BASE_DOMAIN}`
  let secure = true
  if (isLocalDev()) {
    domain = window.location.hostname
    secure = false
  }
  if (!Cookies.get(key)) {
    Cookies.set(key, 'en', {secure, domain, expires: 90})
    if (isIndonesiaTimeZone()) {
      Cookies.set(key, 'id', {secure, domain, expires: 90})
    } else {
      const browserLanguage = getBrowserLanguage()
      if (browserLanguage === 'id') {
        Cookies.set(key, 'id', {secure, domain, expires: 90})
      }
    }
  }
}

const isIndonesiaTimeZone = () => {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const idZones = ['Asia/Jakarta', 'Asia/Makassar', 'Asia/Jayapura', 'Asia/Pontianak']
  return idZones.includes(timeZone)
}

const getBrowserLanguage = () => {
  const nav = window.navigator
  const browserLanguagePropertyKeys = ['language', 'browserLanguage', 'systemLanguage', 'userLanguage']
  let i
  let language

  if (Array.isArray(nav.languages)) {
    for (i = 0; i < nav.languages.length; i++) {
      language = nav.languages[i]
      if (language && language.length) {
        return language
      }
    }
  }

  for (i = 0; i < browserLanguagePropertyKeys.length; i++) {
    language = nav[browserLanguagePropertyKeys[i]]
    if (language && language.length) {
      return language
    }
  }

  return null
}

export const isFileSizeOverLimit = (file, limit) => {
  const size = parseFloat(file.size / (1024 * 1024)).toFixed(2)
  return size > limit
}

/**
 * Format minutes to x hr y min
 * @param minutes - Value of time in minutes
 * @return Return string of formatted time, eg: 1hr 23min
 */
export const formatMinutes = (minutes) => {
  const minutesInHour = 60
  const minutesInDay = 24 * minutesInHour;
  const hours = Math.floor((minutes % minutesInDay) / minutesInHour);
  const remainingMinutes = minutes % minutesInHour;
  return hours > 0 ? `${hours} hr ${remainingMinutes} min` : `${remainingMinutes} min`
}

/**
 * Get range value from period
 * @param period - Period type, eg: thisWeek, thisYear, etc
 * @return Return string of range value to send to API, return empty string if no period type available
 */
export const rangeValueFromPeriod = (period) => {
  switch (period) {
    case 'yesterday': {
      return 'days'
    }
    case 'lastSeven': {
      return 'days'
    }
    case 'lastThirty': {
      return 'days'
    }
    case 'lastNinety': {
      return 'week'
    }
    case 'thisWeek': {
      return 'week'
    }
    case 'thisMonth': {
      return 'month'
    }
    case 'thisYear': {
      return 'year'
    }
    case 'lastWeek': {
      return 'week'
    }
    case 'lastMonth': {
      return 'month'
    }
    case 'custom': {
      return 'custom'
    }
    default:
      return ''
  }
}

export const isServerSide = () => {
  const history = router.history
  return history._startLocation === history.current.fullPath;
}

export const isMarketingMerchantApp = (route) => {
  return route.path.includes('/merchant-app')
}

export const getAuth = (type, id) => {
  const permission = store.state.permission
  if (type === 'root') {
    if (permission.root[id] !== undefined) {
      return permission.root[id]
    }
  } else if (type === 'menu') {
    if (permission.side_menu[id] !== undefined) {
      return permission.side_menu[id]
    }
  } else if (type === 'page') {
    if (permission.page_permissions[id]) {
      return permission.page_permissions[id].enter
    }
  } else if (type === 'component') {
    const path = id.split('#')[0]
    const component = id.split('#')[1]
    if (permission.page_permissions[path] && permission.page_permissions[path].components[component] !== undefined) {
      return permission.page_permissions[path].components[component]
    }
  }
  return true
}

export const isEmptyObject = (obj) => {
  for (const prop in obj) {
    if (Object.hasOwn(obj, prop)) {
      return false
    }
  }
  return true
}

/**
 * Get all possible parent classes from a Vue component's $vnode
 * Handles static classes, dynamic classes (string, array, object formats)
 * 
 * @param {Object} vnode - Vue component's $vnode object
 * @returns {string} - Combined parent class string, trimmed
 * 
 * @example
 * // Static class: <component class="px-4 py-2">
 * // Dynamic class: <component :class="dynamicClass">
 * // Object class: <component :class="{ 'px-4': true, 'py-2': condition }">
 * // Array class: <component :class="['px-4', dynamicClass]">
 * const classes = getParentClasses(this.$vnode)
 * // Returns: "px-4 py-2 other-class"
 */
export const getParentClasses = (vnode) => {
  let parentClass = ''

  // Check $vnode.data for classes (Vue 2)
  if (vnode && vnode.data) {
    const vnodeData = vnode.data

    // Static class (class="...")
    if (vnodeData.staticClass) {
      parentClass += vnodeData.staticClass + ' '
    }

    // Dynamic class (:class="...")
    if (vnodeData.class) {
      if (typeof vnodeData.class === 'string') {
        parentClass += vnodeData.class + ' '
      } else if (Array.isArray(vnodeData.class)) {
        parentClass += vnodeData.class.join(' ') + ' '
      } else if (typeof vnodeData.class === 'object') {
        // Handle object syntax {:class="{ 'px-8': true }"}
        Object.keys(vnodeData.class).forEach(key => {
          if (vnodeData.class[key]) {
            parentClass += key + ' '
          }
        })
      }
    }
  }

  return parentClass.trim()
}

/**
 * Check if a class string contains Vuetify padding classes
 * Matches padding classes: pa-, pt-, pr-, pb-, pl-, px-, py-
 * 
 * @param {string} classString - String of CSS classes to check
 * @returns {RegExpMatchArray|null} - Match result or null if no padding classes found
 * 
 * @example
 * hasPaddingClasses('px-4 py-2 bg-blue') // Returns match array
 * hasPaddingClasses('bg-blue text-white') // Returns null
 * hasPaddingClasses('pa-6') // Returns match array
 * hasPaddingClasses('mx-auto my-4') // Returns null (margin classes don't match)
 */
export const hasPaddingClasses = (classString) => {
  return classString.match(/\b(pa-|pt-|pr-|pb-|pl-|px-|py-)\d+\b/)
}

/**
 * Generate PDF thumbnail from document URL
 * @param {Object} document - Document object with fileUrl and other properties
 * @param {number} width - Thumbnail width (default: 60px)
 * @param {number} height - Thumbnail height (default: 75px)
 * @returns {Promise<void>} - Resolves when thumbnail is generated and added to document.thumbnail
 */
export const generatePdfThumbnail = async (document, width = 60, height = 75) => {
  try {
    document.thumbnailLoading = true

    // Ensure we're in browser context before creating canvas
    if (typeof window === 'undefined' || typeof window.document === 'undefined') {
      console.warn('Canvas not available in current context')
      document.thumbnailLoading = false
      return
    }

    try {
      // Load the PDF document
      const pdf = await pdfjsLib.getDocument(document.fileUrl).promise

      // Get the first page
      const page = await pdf.getPage(1)

      // Create canvas for thumbnail
      const canvas = window.document.createElement('canvas')
      const context = canvas.getContext('2d')

      // Set thumbnail dimensions
      const targetWidth = width
      const targetHeight = height

      // Get original page dimensions
      const viewport = page.getViewport({ scale: 1.0 })

      // Calculate scale to fit within target dimensions while maintaining aspect ratio
      const scaleX = targetWidth / viewport.width
      const scaleY = targetHeight / viewport.height
      const scale = Math.min(scaleX, scaleY)

      const scaledViewport = page.getViewport({ scale })

      // Always use exact target dimensions for canvas
      canvas.width = targetWidth
      canvas.height = targetHeight

      // Fill canvas with background color
      context.fillStyle = '#ffffff'
      context.fillRect(0, 0, targetWidth, targetHeight)

      // Calculate centering offsets
      const offsetX = (targetWidth - scaledViewport.width) / 2
      const offsetY = (targetHeight - scaledViewport.height) / 2

      // Save context and translate for centering
      context.save()
      context.translate(offsetX, offsetY)

      // Render PDF page to canvas
      const renderContext = {
        canvasContext: context,
        viewport: scaledViewport
      }

      await page.render(renderContext).promise

      // Restore context
      context.restore()

      // Convert to thumbnail
      document.thumbnail = canvas.toDataURL('image/jpeg', 0.8)

    } catch (pdfError) {
      console.warn('Failed to load PDF for thumbnail, using fallback:', pdfError)

      // Fallback: Create a styled placeholder with consistent dimensions
      const canvas = window.document.createElement('canvas')
      const context = canvas.getContext('2d')

      // Use configurable thumbnail dimensions
      const targetWidth = width
      const targetHeight = height

      canvas.width = targetWidth
      canvas.height = targetHeight

      // Create a document-like appearance
      context.fillStyle = '#ffffff'
      context.fillRect(0, 0, targetWidth, targetHeight)
      context.strokeStyle = '#e0e0e0'
      context.lineWidth = 1
      context.strokeRect(0.5, 0.5, targetWidth - 1, targetHeight - 1)

      // Add header area (scaled proportionally)
      const headerHeight = Math.max(8, targetHeight * 0.16)
      context.fillStyle = '#f5f5f5'
      context.fillRect(4, 4, targetWidth - 8, headerHeight)

      // Add text lines to simulate document content (scaled proportionally)
      context.fillStyle = '#e0e0e0'
      const lineHeight = Math.max(2, targetHeight * 0.027)
      const lineSpacing = Math.max(6, targetHeight * 0.107)
      const maxLines = Math.floor((targetHeight - headerHeight - 20) / lineSpacing)

      for (let i = 0; i < Math.min(5, maxLines); i++) {
        const y = headerHeight + 8 + (i * lineSpacing)
        const lineWidth = Math.random() * (targetWidth * 0.67) + (targetWidth * 0.17)
        context.fillRect(4, y, lineWidth, lineHeight)
      }

      // Add PDF indicator (scaled proportionally)
      context.fillStyle = '#ff4444'
      const fontSize = Math.max(8, targetWidth * 0.133)
      context.font = `bold ${fontSize}px Arial`
      context.textAlign = 'center'
      context.fillText('PDF', targetWidth / 2, targetHeight - 6)

      document.thumbnail = canvas.toDataURL('image/jpeg', 0.8)
    }

    document.thumbnailLoading = false
  } catch (error) {
    console.error('Error generating PDF thumbnail:', error)
    document.thumbnailLoading = false
  }
}

/**
 * Render PDF fallback when PDF cannot be loaded
 * @param {HTMLCanvasElement} canvas - Canvas element to render fallback to
 * @param {Object} document - Document object with name and other properties
 * @param {number} width - Canvas width (default: 600px)
 * @param {number} height - Canvas height (default: 800px)
 * @returns {void}
 */
export const renderPdfFallback = (canvas, document, width = 600, height = 800) => {
  if (!canvas) {
    document.pdfLoading = false
    return
  }

  const context = canvas.getContext('2d')
  if (!context) {
    document.pdfLoading = false
    return
  }

  canvas.width = width
  canvas.height = height

  // Draw mock PDF content
  context.fillStyle = '#ffffff'
  context.fillRect(0, 0, canvas.width, canvas.height)
  context.strokeStyle = '#dee2e6'
  context.strokeRect(0, 0, canvas.width, canvas.height)

  // Add mock content
  context.fillStyle = '#212529'
  context.font = '16px Arial'
  context.textAlign = 'center'
  context.fillText(document.name, canvas.width / 2, 50)

  context.font = '12px Arial'
  context.textAlign = 'left'
  context.fillText('PDF preview unavailable.', 50, 100)
  context.fillText('The PDF document could not be loaded', 50, 120)
  context.fillText('for preview display.', 50, 140)

  // Add PDF icon
  context.fillStyle = '#ff4444'
  context.font = 'bold 24px Arial'
  context.textAlign = 'center'
  context.fillText('PDF', canvas.width / 2, canvas.height / 2)

  document.pdfLoading = false
}

/**
 * Format filename with truncation while preserving file extension
 * @param {string} filename - Original filename to format
 * @param {number} truncateLength - Maximum length for filename before extension (default: 15)
 * @returns {string} - Formatted filename with extension preserved
 *
 * @example
 * formatFilename('VeryLongDocumentName.pdf', 10) // Returns: "VeryLongDo...pdf"
 * formatFilename('short.jpg', 15) // Returns: "short.jpg"
 * formatFilename('NoExtension', 10) // Returns: "NoExtensio..."
 */
export const formatFilename = (filename, truncateLength = 15) => {
  if (!filename) return ''

  const lastDotIndex = filename.lastIndexOf('.')
  if (lastDotIndex === -1) {
    // No extension, just truncate to specified length
    return filename.length > truncateLength ? filename.substring(0, truncateLength) + '...' : filename
  }

  const nameWithoutExt = filename.substring(0, lastDotIndex)
  const extension = filename.substring(lastDotIndex)

  if (nameWithoutExt.length > truncateLength) {
    return nameWithoutExt.substring(0, truncateLength) + '...' + extension
  }

  return filename
}

/**
 * Load PDF preview to canvas element
 * @param {Object} document - Document object with fileUrl and other properties
 * @param {HTMLCanvasElement} canvas - Canvas element to render PDF to
 * @param {number} containerWidth - Available container width for scaling (default: 550px)
 * @returns {Promise<void>} - Resolves when PDF is loaded and rendered
 */
export const loadPdfPreview = async (document, canvas, containerWidth = 550) => {
  if (!document || document.type !== 'PDF') return

  try {
    document.pdfLoading = true

    try {
      // Load the PDF document
      const pdf = await pdfjsLib.getDocument(document.fileUrl).promise

      if (!canvas) {
        document.pdfLoading = false
        return
      }

      // Ensure canvas context is available
      const context = canvas.getContext('2d')
      if (!context) {
        console.warn('Canvas context not available')
        document.pdfLoading = false
        return
      }

      // Get the first page
      const page = await pdf.getPage(1)

      // Set canvas size to fit container while maintaining aspect ratio
      const viewport = page.getViewport({ scale: 1.0 })
      const scale = containerWidth / viewport.width

      const scaledViewport = page.getViewport({ scale })
      canvas.width = scaledViewport.width
      canvas.height = scaledViewport.height

      // Render PDF page to canvas
      const renderContext = {
        canvasContext: context,
        viewport: scaledViewport
      }

      await page.render(renderContext).promise
      document.pdfLoading = false

    } catch (pageError) {
      console.warn('Failed to render PDF page, using fallback:', pageError)
      renderPdfFallback(canvas, document)
    }

  } catch (pdfError) {
    console.warn('Failed to load PDF for preview, using fallback:', pdfError)
    renderPdfFallback(canvas, document)
  }
}

/**
 * Validate that a numeric value doesn't exceed allowed decimal places
 * @param {number|string} value - The value to validate
 * @param {Object} currency - Currency object with exponent property
 * @param {boolean} returnBoolean - If true, returns boolean; if false, returns validation message
 * @returns {boolean|string} - Validation result (true if valid, error message if invalid)
 *
 * @example
 * // For form validation rules
 * validateDecimalPlaces(12.345, { exponent: 2 }) // Returns: "Maximum 2 decimal places allowed"
 * validateDecimalPlaces(12.34, { exponent: 2 }) // Returns: true
 *
 * // For boolean checks
 * validateDecimalPlaces(12.345, { exponent: 2 }, true) // Returns: false
 * validateDecimalPlaces(12.34, { exponent: 2 }, true) // Returns: true
 */
export const validateDecimalPlaces = (value, currency, returnBoolean = false) => {
  if (value == null || value === '') return returnBoolean ? true : true

  const valueStr = value.toString()
  const decimalIndex = valueStr.indexOf('.')

  if (decimalIndex !== -1) {
    const decimalPlaces = valueStr.length - decimalIndex - 1
    const isValid = decimalPlaces <= currency.exponent

    if (returnBoolean) {
      return isValid
    }

    // Generate appropriate error message
    if (!isValid) {
      if (currency.exponent === 0) {
        return 'No decimal places allowed'
      }
      return `Maximum ${currency.exponent} decimal ${currency.exponent === 1 ? 'place' : 'places'} allowed`
    }
    return true
  }

  return returnBoolean ? true : true
}

/**
 * Check if a value has excessive decimal places
 * @param {number|string} value - The value to check
 * @param {Object} currency - Currency object with exponent property
 * @returns {boolean} - True if excessive decimals, false otherwise
 *
 * @example
 * hasExcessiveDecimals(12.345, { exponent: 2 }) // Returns: true
 * hasExcessiveDecimals(12.34, { exponent: 2 }) // Returns: false
 * hasExcessiveDecimals(null, { exponent: 2 }) // Returns: false
 */
export const hasExcessiveDecimals = (value, currency) => {
  if (value == null || value === '') return false

  const valueStr = value.toString()
  const decimalIndex = valueStr.indexOf('.')

  if (decimalIndex !== -1) {
    const decimalPlaces = valueStr.length - decimalIndex - 1
    return decimalPlaces > currency.exponent
  }

  return false
}

/**
 * Generate panel claim link URL for sharing with panel providers
 * @param {string} companySlug - Company slug identifier
 * @param {string} claimPid - Claim PID (public identifier)
 * @returns {string} - Full URL to the panel claim view
 *
 * @example
 * generatePanelClaimLink('myclinic', 'CLM-12345') // Returns: "https://myclinic.yezza.co/panel/claim/CLM-12345"
 * generatePanelClaimLink(null, 'CLM-12345') // Returns: ""
 * generatePanelClaimLink('myclinic', null) // Returns: ""
 */
export const generatePanelClaimLink = (companySlug, claimPid) => {
  if (!companySlug || !claimPid) return ''
  return `https://${companySlug}.yezza.co/panel/claim/${claimPid}`
}

/**
 * Build a UOM conversion graph using BFS.
 * Mirrors API's update_inventory_uom in product_models.py.
 * @param {Array<{uom: string, convert_to: string, multiplier: number}>} uomConversions
 * @returns {Object} graph mapping UOM name → relative value
 */
export function buildUomGraph(uomConversions) {
  if (!uomConversions || uomConversions.length === 0) return {}
  const relations = uomConversions.map(c => ({
    left: c.uom,
    factor: Number(c.multiplier),
    right: c.convert_to
  }))
  const graph = { [relations[0].left]: 1 }
  let updated = true
  while (updated) {
    updated = false
    for (const { left, factor, right } of relations) {
      if (right in graph && !(left in graph)) {
        graph[left] = factor * graph[right]
        updated = true
      } else if (left in graph && !(right in graph)) {
        graph[right] = graph[left] / factor
        updated = true
      }
    }
  }
  return graph
}

/**
 * Get UOM scale factor for a given UOM relative to the base (smallest) unit.
 * @param {Object} graph - UOM graph from buildUomGraph()
 * @param {string} uom - The UOM to get scale for
 * @returns {number} scale factor (1 if UOM is base or not found)
 */
export function getUomScale(graph, uom) {
  if (!graph || Object.keys(graph).length === 0) return 1
  const sorted = Object.entries(graph).sort((a, b) => a[1] - b[1])
  const baseValue = sorted[0][1]
  const uomValue = graph[uom]
  if (!uomValue) return 1
  return uomValue / baseValue
}

/**
 * Convert a base UOM quantity into equivalent representations for each conversion tier.
 * @param {number} quantity - Quantity in base (smallest) UOM
 * @param {Array<{uom: string, convert_to: string, multiplier: number}>} uomConversions
 * @returns {Array<string>} e.g. ["6 Carton 1 Box 3 Strips", "73 Box 3 Strips"]
 */
export function getUomEquivalents(quantity, uomConversions) {
  if (!quantity || !uomConversions || uomConversions.length === 0) return []
  const graph = buildUomGraph(uomConversions)
  const sorted = Object.entries(graph).sort((a, b) => a[1] - b[1])
  const baseValue = sorted[0][1]
  const baseUom = sorted[0][0]
  const conversions = uomConversions
    .map(conv => ({ uom: conv.uom, scale: graph[conv.uom] / baseValue }))
    .sort((a, b) => b.scale - a.scale)

  const equivalents = []
  for (let startIdx = 0; startIdx < conversions.length; startIdx++) {
    let remaining = quantity
    const parts = []
    for (let i = startIdx; i < conversions.length; i++) {
      const wholeUnits = Math.floor(remaining / conversions[i].scale)
      remaining = remaining % conversions[i].scale
      if (wholeUnits > 0) {
        parts.push(`${wholeUnits} ${conversions[i].uom}`)
      }
    }
    if (remaining > 0) {
      parts.push(`${remaining} ${baseUom}`)
    }
    if (parts.length > 0) {
      equivalents.push(parts.join(' '))
    }
  }
  if (quantity > 0) {
    equivalents.push(`${quantity} ${baseUom}`)
  }
  return equivalents
}